import React, { useEffect, useMemo } from "react";
import QUESTIONNAIRES from "./questionnaireDefs";

// Single generic viewer for ALL questionnaire submissions (2026-09-02),
// replacing the six per-form viewers in htmls/ — those had a tiny ×
// that scrolled out of reach ("View HTML can't be closed") and six
// different styles. Questions/keys come from questionnaireDefs, which
// mirrors the legacy field contract, so legacy AND care-native rows both
// render; scores are recomputed live so even legacy rows get an
// interpretation. Unknown forms (e.g. the stray "Office Visit Notes"
// row) fall back to a raw answer list rather than rendering nothing.
const QuestionnaireView = ({ data, ismodalopen, setHtmlModal }) => {
    const json = data?.json_data || null;

    const def = useMemo(
        () => QUESTIONNAIRES.find((q) => q.formName === data?.form_name) || null,
        [data?.form_name]
    );

    const scored = useMemo(() => {
        if (!def || !json) return null;
        try { return def.score(json); } catch { return null; }
    }, [def, json]);

    useEffect(() => {
        if (!ismodalopen) return;
        const onKey = (e) => { if (e.key === "Escape") setHtmlModal(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [ismodalopen, setHtmlModal]);

    if (!ismodalopen || !json) return null;
    const close = () => setHtmlModal(false);

    const meta = [
        json.patient_name && ["Patient", json.patient_name],
        (json.cdate || json.cdate_5) && ["Date", json.cdate || json.cdate_5],
        json.provider_name && ["Provider", json.provider_name],
        json.dob && ["DOB", json.dob],
    ].filter(Boolean);

    const vitals = def?.hasVitals
        ? [["HT", json.ptheight], ["WT", json.ptweight], ["BMI", json.ptbmi], ["BP", json.ptbp], ["NC", json.ptnc]]
            .filter(([, v]) => v)
        : [];

    const sections = def
        ? (def.sections
            ? def.sections
                .map((s) => ({ title: s.title, items: s.keys.map((k) => def.items.find((i) => i.key === k)).filter(Boolean) }))
                .concat([{ title: "Additional", items: def.items.filter((i) => !def.sections.some((s) => s.keys.includes(i.key))) }])
            : [{ title: "", items: def.items }])
        : null;

    const chip = (label, active) => (
        <span key={String(label)} className={`inline-flex items-center px-3 py-1.5 rounded-full text-[13px] font-font1 border transition-colors ${active
            ? "bg-[#1D2951] text-white border-[#1D2951] font-semibold shadow-sm"
            : "bg-[#F9FAFB] text-[#9CA3AF] border-[#E5E7EB]"}`}>
            {label}
        </span>
    );

    const renderAnswer = (item) => {
        const val = json[item.key];
        if (item.type === "checklist") {
            const arr = Array.isArray(val) ? val : (val ? [val] : []);
            return arr.length
                ? <div className="flex flex-wrap gap-2">{arr.map((v) => chip(v, true))}</div>
                : <span className="text-[13px] text-[#9CA3AF] italic">None reported</span>;
        }
        if (item.type === "scale") {
            return <div className="flex flex-wrap gap-2">{item.options.map((o) =>
                chip(`${o.label} (${o.value})`, Number(val) === o.value && val !== undefined && val !== ""))}</div>;
        }
        if (item.type === "points") {
            return <div className="flex items-center gap-2">
                {chip(`${Number(val) || 0} / ${item.max} points`, true)}
            </div>;
        }
        return <div className="flex flex-wrap gap-2">{item.options.map((o) => chip(o, String(val) === String(o)))}</div>;
    };

    return (
        <div className="fixed inset-0 z-[9600] bg-black/60 backdrop-blur-[2px] flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">

                {/* Sticky header — always-visible close */}
                <div className="px-6 py-4 border-b border-[#E5E7EB] bg-white flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="text-[17px] font-semibold text-[#111827] font-font1 leading-snug">{data.form_name}</div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5">
                            {meta.map(([k, v]) => (
                                <span key={k} className="text-[12px] text-[#6B7280] font-font1">
                                    <span className="font-semibold text-[#374151]">{k}:</span> {v}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button onClick={close} aria-label="Close"
                        className="shrink-0 h-9 w-9 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] text-[16px] flex items-center justify-center transition-colors">
                        ✕
                    </button>
                </div>

                {/* Score banner */}
                {scored && (
                    <div className="px-6 py-3 bg-[#1D2951]/[0.04] border-b border-[#E5E7EB] flex items-center gap-4">
                        <div className="shrink-0 h-12 w-12 rounded-xl bg-[#1D2951] text-white flex flex-col items-center justify-center leading-none">
                            <span className="text-[16px] font-bold">{scored.total}</span>
                            <span className="text-[9px] opacity-70 mt-0.5">/ {scored.max}</span>
                        </div>
                        <div className="text-[13px] text-[#374151] font-font1">{scored.interpretation}</div>
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {vitals.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                            {vitals.map(([k, v]) => (
                                <span key={k} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB] text-[12px] font-font1">
                                    <span className="font-semibold text-[#50869A]">{k}</span>
                                    <span className="text-[#111827]">{v}</span>
                                </span>
                            ))}
                        </div>
                    )}

                    {def?.preamble && (
                        <div className="text-[13px] text-[#6B7280] italic mb-3 font-font1">{def.preamble}</div>
                    )}

                    {sections ? sections.map((section, si) => (
                        (section.items.length > 0) && (
                            <div key={si} className="mb-2">
                                {section.title && (
                                    <div className="text-[12px] font-bold tracking-wide uppercase text-[#50869A] mt-4 mb-2 font-font1">{section.title}</div>
                                )}
                                {section.items.map((item, ii) => (
                                    <div key={item.key} className="py-3 border-b border-[#F3F4F6] last:border-0">
                                        <div className="text-[14px] text-[#111827] mb-2 font-font1 leading-relaxed">
                                            <span className="text-[#9CA3AF] font-medium mr-1.5">{si === 0 && !section.title ? `${ii + 1}.` : ""}</span>
                                            {item.text}
                                        </div>
                                        {renderAnswer(item)}
                                    </div>
                                ))}
                            </div>
                        )
                    )) : (
                        // Unknown form — raw fallback so nothing is ever unviewable.
                        <div>
                            {Object.entries(json)
                                .filter(([k]) => !["_token", "form_id", "form_name", "patient_id", "source"].includes(k))
                                .map(([k, v]) => (
                                    <div key={k} className="py-2 border-b border-[#F3F4F6] text-[13px] font-font1">
                                        <span className="font-semibold text-[#374151] mr-2">{k}:</span>
                                        <span className="text-[#111827]">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-[#E5E7EB] flex items-center justify-between bg-[#FAFAFA]">
                    <span className="text-[11px] text-[#9CA3AF] font-font1">
                        {json.source === "care" ? "Completed in care" : "Completed in legacy forms app"}
                        {data.created_at ? ` · ${String(data.created_at).replace("T", " ").slice(0, 16)}` : ""}
                    </span>
                    <button onClick={close}
                        className="px-4 py-1.5 rounded-lg bg-[#1D2951] text-white text-[13px] font-medium font-font1 hover:opacity-90">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuestionnaireView;
